import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, Checkbox, EmptyState, Input, Select, Stack, Text } from '../components/atoms';
import { Alert } from '../components/atoms/Alert/Alert';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { formatDate, licenseStatusTone } from '../components/admin/adminFormat';
import { ApiError } from '../lib/apiClient';
import {
  createLicense,
  fetchAdminLicenses,
  fetchLicenseSummary,
  revokeLicense,
  type AdminLicense,
  type CreatedLicense,
  type IssuablePlan,
} from '../services/adminService';

const VALIDITY_OPTIONS = ['30', '90', '365', 'unlimited'] as const;
const PLAN_OPTIONS: IssuablePlan[] = ['standard', 'reef', 'reef_pro'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Admin listing → the status vocabulary of the user-facing licence (inactive = revoked). */
function statusKey(license: AdminLicense): 'active' | 'expired' | 'revoked' {
  return license.status === 'inactive' ? 'revoked' : license.status;
}

/**
 * Admin → Licences: summary, issue a licence (apply to the account now, or
 * only create a key the user activates in Settings → Licence), list and revoke
 * (GET/POST/DELETE /api/v1/admin/licenses, GET …/summary).
 */
export function AdminLicensesPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const licenses = useQuery({ queryKey: ['admin', 'licenses'], queryFn: fetchAdminLicenses });
  const summary = useQuery({ queryKey: ['admin', 'licenses', 'summary'], queryFn: fetchLicenseSummary });

  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<IssuablePlan>('standard');
  const [validity, setValidity] = useState<(typeof VALIDITY_OPTIONS)[number]>('90');
  const [apply, setApply] = useState(true);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedLicense | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [done, setDone] = useState('');

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setCreateError(null);
    setCreated(null);
    setCopied(false);
    const address = email.trim();
    if (!EMAIL_PATTERN.test(address)) {
      setEmailError(t('adminArea.licenses.errors.email'));
      return;
    }
    setEmailError(undefined);
    setSubmitting(true);
    try {
      const result = await createLicense({
        email: address,
        plan,
        validDays: validity === 'unlimited' ? undefined : Number(validity),
        apply,
      });
      setCreated(result);
      setEmail('');
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setCreateError(t('adminArea.licenses.errors.userNotFound'));
      else if (err instanceof ApiError && err.status === 503) setCreateError(t('adminArea.licenses.errors.unavailable'));
      else setCreateError(t('adminArea.licenses.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCode(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function askRevoke(license: AdminLicense): void {
    const who = license.userEmail ?? license.userId;
    setDone('');
    setConfirm({
      title: t('adminArea.licenses.revoke'),
      message: t('adminArea.licenses.confirmRevoke', { email: who }),
      confirmLabel: t('adminArea.licenses.revoke'),
      onConfirm: async () => {
        await revokeLicense(license.id);
        setDone(t('adminArea.licenses.revoked', { email: who }));
        await queryClient.invalidateQueries({ queryKey: ['admin'] });
      },
    });
  }

  const rows = licenses.data ?? [];

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.licenses.description')} />
          <AdminArea>
            <Section title={t('adminArea.licenses.summaryTitle')}>
              {summary.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.licenses.loading')}</Text>
                </LiveRegion>
              )}
              {summary.isError && (
                <LoadErrorState
                  compact
                  title={t('adminArea.licenses.summaryError')}
                  onRetry={() => void summary.refetch()}
                />
              )}
              {summary.data && (
                <dl className="rg-admin-summary">
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.licenses.summary.users')}</dt>
                    <dd className="rg-admin-summary__value">{summary.data.totalLicenses}</dd>
                  </div>
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.licenses.summary.licensed')}</dt>
                    <dd className="rg-admin-summary__value">{summary.data.usedLicenses}</dd>
                  </div>
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.licenses.summary.unlicensed')}</dt>
                    <dd className="rg-admin-summary__value">{summary.data.availableLicenses}</dd>
                  </div>
                </dl>
              )}
            </Section>

            <Section title={t('adminArea.licenses.createTitle')} description={t('adminArea.licenses.createDescription')}>
              <Card padding="lg">
                <form onSubmit={(e) => void onSubmit(e)} noValidate>
                  <div className="rg-admin-form">
                    <Input
                      type="email"
                      label={t('adminArea.licenses.email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={emailError}
                      autoComplete="off"
                      required
                      fullWidth
                    />
                    <Select
                      label={t('adminArea.licenses.plan')}
                      value={plan}
                      onChange={(e) => setPlan(e.target.value as IssuablePlan)}
                      options={PLAN_OPTIONS.map((p) => ({ value: p, label: t(`adminArea.plans.${p}`) }))}
                      fullWidth
                    />
                    <Select
                      label={t('adminArea.licenses.validity')}
                      value={validity}
                      onChange={(e) => setValidity(e.target.value as (typeof VALIDITY_OPTIONS)[number])}
                      options={VALIDITY_OPTIONS.map((v) => ({
                        value: v,
                        label: v === 'unlimited' ? t('adminArea.licenses.validityUnlimited') : t('adminArea.licenses.validityDays', { count: Number(v) }),
                      }))}
                      fullWidth
                    />
                  </div>
                  <Stack gap="4" className="rg-admin-form__actions">
                    <Checkbox
                      label={t('adminArea.licenses.applyNow')}
                      checked={apply}
                      onChange={(e) => setApply(e.target.checked)}
                    />
                    <div>
                      <Button type="submit" isLoading={submitting} disabled={submitting}>
                        {apply ? t('adminArea.licenses.submitApply') : t('adminArea.licenses.submitIssue')}
                      </Button>
                    </div>
                  </Stack>
                </form>
              </Card>

              {createError !== null && <Alert>{createError}</Alert>}

              {created !== null && (
                <Alert tone="success" title={created.applied ? t('adminArea.licenses.createdApplied', { email: created.userEmail }) : t('adminArea.licenses.createdIssued', { email: created.userEmail })}>
                  <Stack gap="2">
                    <Text>{created.applied ? t('adminArea.licenses.codeHintApplied') : t('adminArea.licenses.codeHintIssued')}</Text>
                    <code className="rg-admin-code">{created.licenseCode}</code>
                    <div>
                      <Button variant="secondary" size="sm" onClick={() => void copyCode(created.licenseCode)}>
                        {copied ? t('adminArea.licenses.copied') : t('adminArea.licenses.copy')}
                      </Button>
                    </div>
                  </Stack>
                </Alert>
              )}
            </Section>

            <Section title={t('adminArea.licenses.listTitle', { count: rows.length })}>
              <LiveRegion>{done}</LiveRegion>
              {licenses.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.licenses.loading')}</Text>
                </LiveRegion>
              )}
              {licenses.isError && (
                <LoadErrorState title={t('adminArea.licenses.loadError')} onRetry={() => void licenses.refetch()} />
              )}
              {!licenses.isLoading && !licenses.isError && rows.length === 0 && (
                <EmptyState
                  icon={<KeyRound size={48} strokeWidth={1.5} aria-hidden="true" />}
                  headline={t('adminArea.licenses.emptyTitle')}
                  description={t('adminArea.licenses.emptyMessage')}
                  actionLabel={t('adminArea.reload')}
                  onAction={() => void licenses.refetch()}
                  headingLevel={3}
                />
              )}
              {!licenses.isLoading && !licenses.isError && rows.length > 0 && (
                <AdminTable caption={t('adminArea.licenses.tableCaption')}>
                  <thead>
                    <tr>
                      <th scope="col">{t('adminArea.licenses.col.user')}</th>
                      <th scope="col">{t('adminArea.licenses.col.plan')}</th>
                      <th scope="col">{t('adminArea.licenses.col.status')}</th>
                      <th scope="col">{t('adminArea.licenses.col.activatedAt')}</th>
                      <th scope="col">{t('adminArea.licenses.col.expiresAt')}</th>
                      <th scope="col">{t('adminArea.licenses.col.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((license) => (
                      <tr key={license.id}>
                        <th scope="row">{license.userEmail ?? license.userId}</th>
                        <td>{String(t(`adminArea.plans.${license.plan}`, { defaultValue: license.plan }))}</td>
                        <td>
                          <Badge tone={licenseStatusTone(statusKey(license))}>
                            {t(`adminArea.licenseStatus.${statusKey(license)}`)}
                          </Badge>
                        </td>
                        <td className="rg-admin-table__numeric">{formatDate(license.activatedAt, i18n.language)}</td>
                        <td className="rg-admin-table__numeric">
                          {license.expiresAt ? formatDate(license.expiresAt, i18n.language) : t('adminArea.licenses.validityUnlimited')}
                        </td>
                        <td>
                          {statusKey(license) === 'active' ? (
                            <Button variant="secondary" size="sm" onClick={() => askRevoke(license)}>
                              {t('adminArea.licenses.revoke')}
                            </Button>
                          ) : (
                            <Text variant="caption" className="rg-admin-table__muted">—</Text>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </Section>

            <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminLicensesPage;
